import { useEffect, useMemo, useState } from 'react'
import { Camera, CheckCircle2, ImageIcon, RotateCcw, ScanFace, Upload, UserX } from 'lucide-react'
import {
  Callout, ErrorState, FormDialog, KpiGrid, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { toApiProblem } from '@/lib/api/client'
import {
  facePhotoUrl, fetchImage, useFaceMutations, useFaceRegistrations, useFaceSettings,
} from '../../api/geofence'
import { FACE_MATCH_MODES } from '../../types/geofence'
import type { FaceRegistrationView } from '../../types/geofence'
import { CameraCapture } from './CameraCapture'
import { Toggle } from './shared'

/** An image fetched through the authenticated API, released when it leaves the screen. */
export function AuthImage({ url, alt, className }: { url: string; alt: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let objectUrl: string | null = null
    let live = true
    fetchImage(url)
      .then((made) => { objectUrl = made; if (live) setSrc(made) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [url])
  if (failed) return <span className="text-xs text-[var(--color-muted)]">Photo unavailable</span>
  if (!src) return <Skeleton className={className} />
  return <img src={src} alt={alt} className={className} />
}

function SettingsCard() {
  const settings = useFaceSettings()
  const { saveSettings } = useFaceMutations()
  const [draft, setDraft] = useState<{ consentText: string; photoRetentionDays: number; deleteOnExit: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (settings.isPending) return <Skeleton className="h-32" />
  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
  const data = settings.data

  return (
    <Card className="p-4">
      {draft && (
        <FormDialog
          open
          onOpenChange={(open) => { if (!open) setDraft(null) }}
          title="Face settings"
          size="lg"
          loading={saveSettings.isPending}
          error={error}
          onSubmit={() => saveSettings.mutate(draft, {
            onSuccess: () => setDraft(null),
            onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'Could not save.'),
          })}
        >
          <div className="space-y-1">
            <Label htmlFor="fs-consent" required>Consent shown before registering</Label>
            <Textarea id="fs-consent" rows={6} maxLength={2000} value={draft.consentText}
              onChange={(event) => setDraft({ ...draft, consentText: event.target.value })} />
            <p className="text-xs text-[var(--color-muted)]">
              Each registration keeps the exact words agreed to, so changing this does not change past consent.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fs-retention" required>Keep punch photos for (days)</Label>
            <Input id="fs-retention" type="number" min={1} max={3650} value={draft.photoRetentionDays}
              onChange={(event) => setDraft({ ...draft, photoRetentionDays: Number(event.target.value || 90) })} />
          </div>
          <Toggle id="fs-exit" label="Delete a person’s face when they leave"
            hint="Checked nightly. Registration photos and face data are removed permanently."
            checked={draft.deleteOnExit} onChange={(on) => setDraft({ ...draft, deleteOnExit: on })} />
        </FormDialog>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 text-sm">
          <p className="font-semibold text-[var(--color-heading)]">Face matching engine</p>
          <p className="flex items-center gap-2">
            <StatusBadge status={data.engineAvailable ? 'ACTIVE' : 'DOWN'}
              tone={data.engineAvailable ? 'success' : 'danger'}
              label={data.engineAvailable ? 'Running on this server' : 'Unavailable'} />
            <span className="text-xs text-[var(--color-muted)]">{data.modelId}</span>
          </p>
          <p className="text-[var(--color-body)]">
            Punch photos kept {data.photoRetentionDays} days · leavers’ faces {data.deleteOnExit ? 'deleted automatically' : 'kept until reset'}
          </p>
        </div>
        <PermissionGate permission="attendance:write">
          <Button type="button" variant="outline" size="sm"
            onClick={() => { setError(null); setDraft({ consentText: data.consentText, photoRetentionDays: data.photoRetentionDays, deleteOnExit: data.deleteOnExit }) }}>
            Edit settings
          </Button>
        </PermissionGate>
      </div>
      {!data.engineAvailable && (
        <p className="mt-2 text-xs text-[var(--color-danger)]">
          While the engine is unavailable, punches are accepted and marked “face not checked” instead of being refused.
        </p>
      )}
    </Card>
  )
}

function PhotosDialog({ row, onClose }: { row: FaceRegistrationView; onClose: () => void }) {
  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose() }}
      title={`Registered face — ${row.employeeName ?? row.employeeCode}`}
      description="Opening these photos is recorded in the access log."
      submitLabel="Close" onSubmit={onClose}>
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: row.photoCount }, (_, index) => (
          <AuthImage key={index} url={facePhotoUrl(row.employeeId, index)} alt={`Registration photo ${index + 1}`}
            className="size-32 rounded object-cover" />
        ))}
      </div>
    </FormDialog>
  )
}

function ResetDialog({ row, onClose }: { row: FaceRegistrationView; onClose: () => void }) {
  const { reset } = useFaceMutations()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose() }}
      title={`Reset ${row.employeeName ?? row.employeeCode}’s face?`}
      description="The face data and registration photos are deleted permanently. They can then register again."
      submitLabel="Reset face" loading={reset.isPending} error={error}
      onSubmit={() => {
        if (!reason.trim()) { setError('Say why.'); return }
        reset.mutate({ employeeId: row.employeeId, reason: reason.trim() }, {
          onSuccess: onClose,
          onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'Could not reset.'),
        })
      }}>
      <div className="space-y-1">
        <Label htmlFor="reset-reason" required>Reason</Label>
        <Input id="reset-reason" value={reason} maxLength={500} placeholder="e.g. New glasses, keeps failing; registered a wrong photo"
          onChange={(event) => setReason(event.target.value)} />
      </div>
    </FormDialog>
  )
}

/** HR registering a face for somebody — from the camera at the HR desk, or from photo files. */
function RegisterForDialog({ row, onClose }: { row: FaceRegistrationView; onClose: () => void }) {
  const { registerFor } = useFaceMutations()
  const [mode, setMode] = useState<'choose' | 'camera'>('choose')
  const [consent, setConsent] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const submit = (photos: Blob[]) => {
    setError(null)
    registerFor.mutate({ employeeId: row.employeeId, photos }, {
      onSuccess: onClose,
      onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'Registration failed.'),
    })
  }

  if (mode === 'camera') {
    return (
      <CameraCapture open title={`Register ${row.employeeName ?? row.employeeCode}`}
        description="Three photos of the employee, a slightly different angle each time."
        count={3} prompts={['Straight at the camera', 'Head slightly left', 'Head slightly right']}
        confirmLabel="Register" busy={registerFor.isPending} error={error}
        onConfirm={(shots) => submit(shots.map((shot) => shot.blob))} onCancel={onClose} />
    )
  }

  return (
    <FormDialog open onOpenChange={(open) => { if (!open) onClose() }}
      title={`Register a face for ${row.employeeName ?? row.employeeCode}`}
      description="For people without a smartphone, or who need help. 2–5 clear photos of only this person."
      submitLabel="Register from files" loading={registerFor.isPending} error={error}
      onSubmit={() => {
        if (!consent) { setError('Confirm the employee has agreed.'); return }
        if (files.length < 2 || files.length > 5) { setError('Choose 2 to 5 photos.'); return }
        submit(files)
      }}>
      <Toggle id="hr-consent" label="The employee has read the consent wording and agreed"
        checked={consent} onChange={setConsent} />
      <div className="space-y-1">
        <Label htmlFor="hr-files">Photo files (JPEG or PNG)</Label>
        <Input id="hr-files" type="file" accept="image/jpeg,image/png,image/webp" multiple
          onChange={(event) => setFiles(Array.from(event.target.files ?? []))} />
      </div>
      <div className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
        or
        <Button type="button" variant="outline" size="sm" disabled={!consent}
          onClick={() => { setError(null); setMode('camera') }}>
          <Camera className="size-4" aria-hidden="true" />
          Use this device’s camera
        </Button>
      </div>
    </FormDialog>
  )
}

type Filter = 'all' | 'needed' | 'registered' | 'missing'

/** Who has registered a face, and whom the policy is waiting on. */
export function FaceTab() {
  const registrations = useFaceRegistrations()
  const [filter, setFilter] = useState<Filter>('needed')
  const [search, setSearch] = useState('')
  const [viewing, setViewing] = useState<FaceRegistrationView | null>(null)
  const [resetting, setResetting] = useState<FaceRegistrationView | null>(null)
  const [registering, setRegistering] = useState<FaceRegistrationView | null>(null)

  const needsFace = (row: FaceRegistrationView) => row.faceMatch != null && row.faceMatch !== 'OFF'
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (registrations.data ?? [])
      .filter((row) => filter === 'all' ? true
        : filter === 'needed' ? needsFace(row)
          : filter === 'registered' ? row.registered
            : needsFace(row) && !row.registered)
      .filter((row) => !term || `${row.employeeName ?? ''} ${row.employeeCode} ${row.departmentName ?? ''}`
        .toLowerCase().includes(term))
  }, [registrations.data, filter, search])

  const all = registrations.data ?? []
  const needed = all.filter(needsFace)

  return (
    <div className="space-y-4">
      {viewing && <PhotosDialog row={viewing} onClose={() => setViewing(null)} />}
      {resetting && <ResetDialog row={resetting} onClose={() => setResetting(null)} />}
      {registering && <RegisterForDialog row={registering} onClose={() => setRegistering(null)} />}

      <SettingsCard />

      <Callout title="How registration works">
        Employees register their own face in the app or on the web Punch page: three photos, after agreeing
        to the consent wording. It is active at once. To change a face, reset it here and they register again.
      </Callout>

      {registrations.isPending && <Skeleton className="h-48" />}
      {registrations.isError && <ErrorState error={registrations.error} onRetry={() => void registrations.refetch()} />}
      {registrations.data && (
        <>
          <KpiGrid items={[
            { label: 'Face check required', value: needed.length, icon: ScanFace, tint: 'purple' },
            { label: 'Registered', value: needed.filter((row) => row.registered).length, icon: CheckCircle2, tint: 'green' },
            { label: 'Still to register', value: needed.filter((row) => !row.registered).length, icon: UserX, tint: 'orange' },
          ]} />

          <div className="flex flex-col gap-2 sm:flex-row">
            <Input aria-label="Search employees" placeholder="Search name, code or department…"
              value={search} onChange={(event) => setSearch(event.target.value)} />
            <Select aria-label="Show" value={filter} className="sm:w-64"
              onChange={(event) => setFilter(event.target.value as Filter)}>
              <option value="needed">Face check required</option>
              <option value="missing">Still to register</option>
              <option value="registered">Registered</option>
              <option value="all">Everyone</option>
            </Select>
          </div>

          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
                  <th className="p-3 text-start font-medium">Employee</th>
                  <th className="p-3 text-start font-medium">Policy</th>
                  <th className="p-3 text-start font-medium">Face</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 300).map((row) => (
                  <tr key={row.employeeId} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3">
                      <p className="font-medium text-[var(--color-heading)]">{row.employeeName}</p>
                      <p className="text-xs text-[var(--color-muted)]">{row.employeeCode}{row.departmentName ? ` · ${row.departmentName}` : ''}</p>
                    </td>
                    <td className="p-3">
                      <p>{row.policyName ?? '—'}</p>
                      <p className="text-xs text-[var(--color-muted)]">
                        {row.faceMatch ? FACE_MATCH_MODES[row.faceMatch].label : 'No policy'}
                      </p>
                    </td>
                    <td className="p-3">
                      {row.registered
                        ? (
                          <>
                            <StatusBadge status="REGISTERED" tone="success" label="Registered" />
                            <p className="mt-1 text-xs text-[var(--color-muted)]">
                              {row.registeredAt ? new Date(row.registeredAt).toLocaleDateString() : ''}
                              {row.source === 'HR' ? ' · by HR' : ''}
                            </p>
                          </>
                        )
                        : <StatusBadge status="NONE" tone={needsFace(row) ? 'warning' : 'neutral'} label="Not registered" />}
                    </td>
                    <td className="p-3 text-end">
                      <span className="inline-flex gap-1">
                        {row.registered && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => setViewing(row)}>
                            <ImageIcon className="size-4" aria-hidden="true" /> Photos
                          </Button>
                        )}
                        <PermissionGate permission="attendance:write">
                          {row.registered
                            ? (
                              <Button type="button" size="sm" variant="outline" onClick={() => setResetting(row)}>
                                <RotateCcw className="size-4" aria-hidden="true" /> Reset
                              </Button>
                            )
                            : (
                              <Button type="button" size="sm" variant="outline" onClick={() => setRegistering(row)}>
                                <Upload className="size-4" aria-hidden="true" /> Register for them
                              </Button>
                            )}
                        </PermissionGate>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="p-4 text-sm text-[var(--color-muted)]">Nobody matches.</p>}
            {rows.length > 300 && (
              <p className="p-3 text-xs text-[var(--color-muted)]">Showing 300 of {rows.length}. Search to narrow it down.</p>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
